import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CircleDot } from "lucide-react";

/**
 * Universal trade-progress bar:
 *   SL (red, left) ─── E ─── T1 ─── T2 (green, right)
 * for BOTH bullish and bearish setups.
 * Progress = how far the trade has moved toward target (0 = SL, 1 = T2).
 */

// Map a price into 0..1 progress where 0 = SL (loss) and 1 = T2 (full win)
function priceToProgress(price, entry, sl, t2, bullish) {
  if (price == null || entry == null) return 0.5;
  if (bullish) {
    if (t2 === sl) return 0.5;
    return Math.max(0, Math.min(1, (price - sl) / (t2 - sl)));
  }
  // bearish: lower price is better → invert
  if (sl === t2) return 0.5;
  return Math.max(0, Math.min(1, (sl - price) / (sl - t2)));
}

export default function TradeProgress({ signal }) {
  const [livePrice, setLivePrice] = useState(signal?.current_price ?? null);

  useEffect(() => {
    setLivePrice(signal?.current_price ?? null);
  }, [signal?.symbol, signal?.current_price]);

  useEffect(() => {
    if (!signal?.symbol) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/price/${encodeURIComponent(signal.symbol)}`);
        if (!cancelled && typeof data?.price === "number") setLivePrice(data.price);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [signal?.symbol]);

  const { status, nowProgress, entryProgress, t1Progress, pctFromEntry, bullish } = useMemo(() => {
    if (!signal) return { status: "—", nowProgress: 0.5, entryProgress: 0.5, t1Progress: 0.5, pctFromEntry: 0, bullish: true };
    const { entry_price: E, stop_loss: SL, target_2: T2, target_1: T1, trend } = signal;
    const bull = trend === "bullish";
    const price = livePrice ?? E;
    let st = "Active · within setup";
    if (bull) {
      if (price <= SL) st = "⚠ Stopped out (SL hit)";
      else if (price >= T2) st = "🎯 T2 hit — full target";
      else if (price >= T1) st = "🎯 T1 hit — partial booking";
    } else {
      if (price >= SL) st = "⚠ Stopped out (SL hit)";
      else if (price <= T2) st = "🎯 T2 hit — full target";
      else if (price <= T1) st = "🎯 T1 hit — partial booking";
    }
    return {
      status: st,
      nowProgress: priceToProgress(price, E, SL, T2, bull),
      entryProgress: priceToProgress(E, E, SL, T2, bull),
      t1Progress: priceToProgress(T1, E, SL, T2, bull),
      pctFromEntry: E ? ((price - E) / E) * 100 * (bull ? 1 : -1) : 0,
      bullish: bull,
    };
  }, [signal, livePrice]);

  if (!signal) return null;

  const fmt = (n) => Number(n).toLocaleString("en-IN", { maximumFractionDigits: 4 });

  // Always render left→right as SL → E → T1 → T2
  const slPct = 0;
  const ePct = entryProgress * 100;
  const t1Pct = t1Progress * 100;
  const t2Pct = 100;
  const nowPct = nowProgress * 100;

  return (
    <div className="pt-2 border-t border-zinc-900" data-testid="trade-progress">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#007AFF]">
          ◢ Trade progress <span className="text-zinc-600">· {bullish ? "long" : "short"}</span>
        </div>
        <div className="text-[10px] font-mono text-zinc-400" data-testid="tp-status">{status}</div>
      </div>

      <div className="relative h-8 pt-2" data-testid="tp-track">
        <div className="absolute inset-x-0 top-5 h-2 bg-gradient-to-r from-[#EF4444]/55 via-zinc-700 to-[#22C55E]/55" />

        <div className="absolute top-2 h-6" style={{ left: `${slPct}%`, transform: "translateX(-50%)" }} title={`SL ${fmt(signal.stop_loss)}`}>
          <div className="h-3 w-0.5 bg-[#EF4444] mx-auto" />
          <div className="text-[9px] font-mono text-[#EF4444] mt-0.5 text-center">SL</div>
        </div>
        <div className="absolute top-2 h-6" style={{ left: `${ePct}%`, transform: "translateX(-50%)" }} title={`Entry ${fmt(signal.entry_price)}`}>
          <div className="h-3 w-0.5 bg-white mx-auto" />
          <div className="text-[9px] font-mono text-white mt-0.5 text-center">E</div>
        </div>
        <div className="absolute top-2 h-6" style={{ left: `${t1Pct}%`, transform: "translateX(-50%)" }} title={`T1 ${fmt(signal.target_1)}`}>
          <div className="h-3 w-0.5 bg-[#22C55E] mx-auto" />
          <div className="text-[9px] font-mono text-[#22C55E] mt-0.5 text-center">T1</div>
        </div>
        <div className="absolute top-2 h-6" style={{ left: `${t2Pct}%`, transform: "translateX(-50%)" }} title={`T2 ${fmt(signal.target_2)}`}>
          <div className="h-3 w-0.5 bg-[#22C55E] mx-auto" />
          <div className="text-[9px] font-mono text-[#22C55E] mt-0.5 text-center">T2</div>
        </div>

        <div
          className="absolute top-[14px] z-10"
          style={{ left: `${nowPct}%`, transform: "translateX(-50%)" }}
          title={`Now ${fmt(livePrice ?? signal.entry_price)}`}
          data-testid="tp-now-dot"
        >
          <CircleDot className="h-4 w-4 text-[#007AFF] fill-[#0A0A0A]" strokeWidth={2.5} />
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
        <div className="text-zinc-500">
          NOW <span className="text-white" data-testid="tp-now-price">{fmt(livePrice ?? signal.entry_price)}</span>
        </div>
        <div className={pctFromEntry >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"} data-testid="tp-pct">
          {pctFromEntry >= 0 ? "+" : ""}{pctFromEntry.toFixed(3)}% vs entry
        </div>
      </div>
    </div>
  );
}
